import {
  GridCellProps,
  GridProps
} from '@progress/kendo-react-grid';
import { SortDescriptor } from '@progress/kendo-data-query';
import { RequestDataBase, SearchRecordBy } from '../../dispatcher/Types';
import React, { KeyboardEvent, ReactElement } from 'react';
import { POS_LIST_ITEM } from './detail/list/Types';

export interface BASE_GRID_PROPS extends NAVIGATION_GRID_PROPS {
}

export interface BASE_GRID_STATE extends BASE_GRID_PROPS {
}

export interface NAVIGATION_BASE_EDITOR_GRID_PROPS extends NAVIGATION_GRID_PROPS {
  /**
   * Exit Edit event handler
   * @param {string | number | boolean} value
   * @param {[key: string]: any} options Other params like data, selectedState, index, mode...
   */
  onExitEdit?: (value: string, options: {
    data: {
      id: string
      inEdit?: any
      name: string
      selected?: boolean
      text: string
    }[]
    field: string
    uniqueField: string
  }) => void
  /**
   * Trigger when is change value of some editable field
   */
  onFieldsValidation?: (isAllEditableFieldsValid: boolean) => void
}

export type CELL_RENDERER_FUNCTION = (cellProps: GridCellProps) => ReactElement<HTMLTableElement>;

export interface TAB_KEY_EVENT_PROPS {
  changedScrollTop: boolean
  currentScrollTop: number
  holding: boolean
  intoContainer: boolean
  shift: boolean
  waitScrollCounter: number
  waitScrollNumber: number
}

export enum SELECT_WITHIN_ROW {
  none,
  first,
  last
}

enum EDITOR_NAMES {
  combo = 'combo',
  numeric = 'numeric',
  text = 'text'
}

export enum RELOAD_TYPE {
  soft = 'soft',
  hard = 'hard'
}

export interface EDITOR {
  comboData?: string[]
  editorName?: EDITOR_NAMES
}

export interface NAVIGATION_BASE_EDITOR_GRID_STATE {
  currentEditorState?: any
  currentFilterValue: string
  data: any[]
  editField?: string
}

export interface REQUEST_TREE_LIST_CONFIG {
  url: string
  method: string
  urlParams?: {}
  params?: {}
  headers?: {}
  treeListRequestParamsMap: {
    rowId: string
    rowIdMap: string
    rowRepeatId?: string
    subRowIdMap?: string
    subRowId?: string
  }
}

export type INSERT_RECORDS = (records: { [key: string]: any }[], index: number) => void;

export interface ADDITIONAL_PARSE_GRID_DATA {
  allRemoteData: any[],
  prevPageData: any[],
  nextPageData: any[],
  pageNumber: number,
  skip: number,
  pageSize: number
}

export interface NAVIGATION_GRID_PROPS extends GridProps {
  preselectedRowIds?: string[]
  accessibilityCaption?: string
  accessibilityRowDescription?: string
  autoHeightCell?: boolean
  autoHeightRowCorrection?: boolean
  beforeContextMenuOpen?: (e: React.MouseEvent, dataItem: any) => any
  beforeSelectionChange?: (dataItem: any, options: any) => boolean
  CELL_RENDER_MAP?: {
    [key: string]: CELL_RENDERER_FUNCTION
  }
  columns: any[]
  columnsMapMethod?: (column: any, index: number) => JSX.Element
  contextMenu?: [any] | [any, { [key: string]: any }]
  /**
   * Editor cell renderer
   * @param cellProps
   * @param column
   * @param valueItem
   */
  customCellRender?: (cellProps, column, valueItem) => JSX.Element
  data?: any[]
  dispatcherId?: string
  emptyGrid?: { img?: string, text?: string }
  filterAction?: string
  filterData?: {}
  gridType?: 'grid' | 'tree'
  getRecord?: (search: any, searchBy: SearchRecordBy) => void
  getRecordIndex?: (item: any) => number
  /**
   * Listens reordering rows event and returns new reorder data
   * @param {any[]} reorderData
   * @param {any} dataItem
   * @param {String} direction There can be 'before' or 'after'
   */
  getReorderingRowsData?: (reorderData: any[], dataItem: any, direction: 'before' | 'after') => any[]
  /**
   * Listens sort event and returns local sorted data
   * @param {Object[]} data State data
   * @param {String} field Column field name (dataIndex)
   * @param {String} sortType There can be 'asc' or 'desc'
   * @param {Object[]} sortedData Sorted state data
   */
  getSortedData?: (data: any[], field: string, sortType: string, sortedData: any[]) => any[]
  /**
   * Selection row event handler
   * @param {any} dataItem
   * @param {any} options Other params like data, selectedState, index, mode...
   * @param {any} newState New state
   */
  handleSelection?: (dataItem: any, options: any, newState: any) => void
  /**
   * Parser for column that could have subItems
   * @param dataItem
   * @param columnDataIndex
   */
  hasExpandDetailButton?: (dataItem: any, columnDataIndex: string) => string | boolean
  height?: number
  id?: string
  /**
   * Function that checks if some list (that has 'treeDataItem' record) item has focus
   * @param {any} treeDataItem
   */
  isListItemFocused?: (treeDataItem: any) => boolean
  isTree?: boolean
  isVirtualScrollHOC?: boolean
  keyboardShortcuts?: boolean
  loadExpandedData?: (records?: any) => void
  moduleId?: string
  nonResizableColumns?: string[]
  nonReordeableColumns?: string[]
  /**
   * Add row event handler
   * @param {any} dataItem
   * @param {any} options Other params like data, selectedState, index, mode...
   */
  onAddRow?: (dataItem: any, options: any) => void
  onAnyKeyDown?: Function
  /**
   * Checkbox change event handler. There can be in mode 'single' or 'all'.
   * Listens single checkbox change and all checkbox change from header.
   * @param {Boolean} value
   * @param {any} options Other params like data, selectedState, index, mode...
   */
  onCellCheckboxChange?: (value: boolean, options: any) => void
  /**
   * Delete row event handler
   * @param {any} dataItem
   * @param {any} options Other params like data, selectedState, index, mode...
   */
  onDeleteRow?: (dataItem: any, options: any) => void
  /**
   * Trigger when the sort and filter changes on the grid
   */
  onFilterAndSortChange?: (data: FILTER_AND_SORT_DATA) => void
  onPageDownKey?: (e: KeyboardEvent) => void
  onPageUpKey?: (e: KeyboardEvent) => void
  onRowControls?: Function
  /**
   * Listener that triggers if row is deselected
   */
  onRowDeselected?: Function
  /**
   * Trigger when user click on row
   */
  onRowSelected?: Function
  parseGridData?: (data: any, INSERT_RECORDS?, additionalData?: ADDITIONAL_PARSE_GRID_DATA) => { [key: string]: any }
  parseCellData?: (data: any) => any
  pageNumberName?: string
  pageSize?: number
  pageSizeName?: string
  /**
   * If true, rows are draggable
   */
  reorderingRows?: boolean
  /**
   * If true, all rows are deselected on reload grid
   */
  resetSelectionOnReload?: boolean
  requestActionName?: string
  requestConfig?: RequestDataBase
  requestData?: (pageNumber: number, skip: number) => void
  /**
   * If true, request will not be performed
   */
  requestInProgress?: boolean
  requestTreeListConfig?: REQUEST_TREE_LIST_CONFIG
  rowHeight?: number
  /**
   * Adds a background to the row so that it looks like when it is selected (without borders)
   * The value must be the same as the value from the unique field of the row
   */
  rowMarked?: string | number
  rowSkeletonEnabled?: boolean
  /**
   * This is the name of dataIndex of header column that has type 'checkbox' (eg. 'checkboxAll')
   */
  selectableAll?: string
  /**
   * Sets forceExpanded property to be true or false
   * @param {boolean} forceExpanded
   */
  setForceExpanded?: (forceExpanded?: boolean) => void
  /**
   * Function that sets focus to list item with position 'pos'
   * @param {any} treeDataItem
   * @param {POS_LIST_ITEM} pos
   */
  setFocusToTreeList?: (treeDataItem: any, pos?: POS_LIST_ITEM) => void
  /**
   * Set data of grid
   * @param {any[]} data
   * @param {boolean} forceSkip
   */
  setGridData?: (data: any[], forceSkip?: boolean) => void
  /**
   * Set lastSelectedItemId in List
   * Trigger if user focus last element of last child with shift-tab (form the record below)
   * @param {string} dataItemId
   * @param {string} id
   */
  setLastSelectedListItemId?: (dataItemId: string, id: string) => void
  sortAction?: string
  shouldUseSearchBar?: boolean
  syncRequestData?: (pageNumber: number, skip: number) => void
  uniqueField?: string
  uniqueFieldDetail?: string
  updateRecord?: any
  updateRecords?: (items) => void
  updateRecordsByIndex?: (items: any[], index: number) => void
  filterConfig?: {
    fields: {
      dataIndex: string,
      label: string,
      flexWidth?: number,
      type: string,
    }[][],
    endPoints?: {
      filters: string
    }
  },
  insertRecords?: INSERT_RECORDS,
  onExpandDetailButtonChange?: (dataItem: any) => void
  onExpandTextButtonChange?: (dataItem: any) => void
}

export interface NAVIGATION_GRID_COLUMN_CONFIG {
  cellType?: string
  dataIndex?: string
  columnMenuConfig?: any
  headerAlign?: string
  className?: string
  headerClassName?: string
  headerCellType?: string
  width?: number
  isGrouped?: boolean
  hidden?: boolean
}

export interface NAVIGATION_GRID_STATE {
  activeItem: any
  activeRowIndex?: any
  columns: NAVIGATION_GRID_COLUMN_CONFIG[]
  contextMenuItem?: any
  currentFilterValue: string
  data: any[]
  dataLoadDone: boolean
  editField?: string
  filterData: {}
  offset: {
    left: number
    top: number
  }
  pageDataPopulated: number[]
  pageNumber: number
  requestInProgress: boolean
  selectedState: {}
  showContextMenu: boolean
  skip: number
  sort: any[]
  tasks: {}
  total: number
  addedTotal: number
}

export interface NAVIGATION_BASE_TREE_GRID_PROPS extends NAVIGATION_GRID_PROPS {
  contextMenuDetail?: any
  /**
   Specifies the name of the property indicating the count of sub-items.
   This should match the property name received from the server that represents the number of sub-items.
   Used for displaying the expand button for rows with sub-items.
   */
  detailExpandedButton?: string,
  /**
   * Sets how many detail list rows will be displayed
   */
  displayDetailListRows?: number,
  /**
   * Sets how many detail list rows will be rendered at all times
   */
  renderedDetailListRows?: number
  dataItem?: any
  field?: string
  onDetailItemDoubleClick?: Function
  /**
   * Parse grid of details
   * @param {any[]} data
   */
  parseGridExtendedData?: (data: any, treeListOtherParams: any) => { [key: string]: any }
  uniqueFieldDetail?: string
  shouldUseSearchBar?: boolean
  isPreloadedSubitems?: boolean
}

export interface NAVIGATION_BASE_TREE_GRID_STATE {
  data: any[],
  expanded: boolean,
}

export interface FILTER_AND_SORT_DATA {
  filterData?: any
  filterType: 'onFilterChange' | 'onSortChange'
  isFilterChanged?: boolean
  params: any
  sort?: SortDescriptor[]
}

export interface EXPAND_DETAIL_BUTTON_SYNTHETIC_EVENT {
  value: boolean
  dataItem: any
  dataIndex: number
}
